/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

const liveSearchUrl = {
    currentCategoryPath: window.liveSearchConfig.currentCategoryPath,
    lastVisitedCategoryPath: '',
    urlParams: new URLSearchParams(window.location.search),
    prevUrl: null,
    nextUrl: null,
    pendingUrlFacets: [],
    facetAttributeAllowlist: null,
    hasAppliedUrlFacets: false,
    hasTriggeredUrlFacetReload: false,
    /**
     * Decode a URL value safely without throwing on malformed sequences.
     *
     * @param {string} value
     * @returns {string}
     */
    safeDecode(value) {
        if (typeof value !== 'string') {
            return value
        }

        try {
            return decodeURIComponent(value)
        } catch (error) {
            console.error(`Failed to decode URL value "${value}": ${error}`)
            return value
        }
    },
    /** Build allowlist of facet attributes from Live Search response facets. */
    setFacetAttributeAllowlist(facets) {
        const attributes = Array.isArray(facets)
            ? facets.map(facet => this.safeDecode(facet.attribute))
            : []
        this.facetAttributeAllowlist = new Set(attributes)
    },
    /**
     * Check if a facet attribute is allowed based on Live Search response.
     *
     * @param {string} attribute
     * @returns {boolean}
     */
    isFacetAttributeAllowed(attribute) {
        if (!this.facetAttributeAllowlist) {
            return false
        }

        return this.facetAttributeAllowlist.has(attribute)
    },
    /**
     * Decode a query param value while preserving a trailing "+" if present
     * in the raw query string (e.g., "Serie+PRFRMNC+").
     *
     * @param {string} rawValue
     * @returns {string}
     */
    decodeQueryValue(rawValue) {
        if (typeof rawValue !== 'string') {
            return rawValue
        }

        const hasTrailingPlus = rawValue.endsWith('+')
        const normalized = rawValue.replace(/\+/g, ' ')
        const decoded = this.safeDecode(normalized)

        if (hasTrailingPlus) {
            return `${decoded.replace(/\s+$/, '')}+`
        }

        return decoded
    },
    /**
     * Parse raw query parameters without URLSearchParams "+" decoding.
     *
     * @returns {Array<{key: string, value: string}>}
     */
    getRawQueryParams() {
        const query = window.location.search || ''
        if (query.length <= 1) {
            return []
        }

        return query
            .substring(1)
            .split('&')
            .filter(Boolean)
            .map(pair => {
                const [rawKey, ...rawValueParts] = pair.split('=')
                return {
                    key: rawKey || '',
                    value: rawValueParts.length ? rawValueParts.join('=') : ''
                }
            })
    },
    /**
     * Normalize raw facet options to an array of trimmed values.
     *
     * @param {string|string[]} rawValue
     * @returns {string[]}
     */
    normalizeFacetOptions(rawValue) {
        const values = Array.isArray(rawValue) ? rawValue : [rawValue]

        return values
            .flatMap(value => {
                if (value === null || value === undefined) {
                    return []
                }

                return String(value).split(',')
            })
            .map(value => this.safeDecode(value))
            .map(value => value.trim())
            .filter(value => value.length > 0)
    },
    /**
     * Normalize facet options from raw query params, preserving trailing "+".
     *
     * @param {string|string[]} rawValue
     * @returns {string[]}
     */
    normalizeFacetOptionsFromRaw(rawValue) {
        const values = Array.isArray(rawValue) ? rawValue : [rawValue]

        return values
            .flatMap(value => {
                if (value === null || value === undefined) {
                    return []
                }

                return String(value).split(',')
            })
            .map(value => this.decodeQueryValue(value))
            .map(value => value.trim())
            .filter(value => value.length > 0)
    },
    /**
     * Add a facet if it does not already exist.
     *
     * @param {string} attribute
     * @param {string} option
     */
    addFacet(attribute, option) {
        const exists = this.selectedFacets.some(
            facet => facet.attribute === attribute && facet.option === option
        )

        if (!exists) {
            this.selectedFacets.push({ attribute, option })
        }
    },
    /**
     * Add or update URL parameters
     *
     * @param {string} key
     * @param {string} value
     */
    addOrUpdateUrlParam(key, value) {
        try {
            this.urlParams.set(key, value)
            this.updateUrl()
        } catch (error) {
            console.error(`Failed to add or update URL parameter: ${error}`)
        }
    },
    /**
     * Remove URL parameter
     *
     * @param {string} key
     * @param {boolean} update
     */
    removeUrlParam(key, update = true) {
        try {
            this.urlParams.delete(key)
            if (update) {
                this.updateUrl()
            }
        } catch (error) {
            console.error(`Failed to remove URL parameter: ${error}`)
        }
    },
    /**
     * Update the URL with current parameters
     */
    updateUrl() {
        try {
            const paramsString = this.urlParams.toString()
            const updatedUrl = paramsString
                ? `${window.location.pathname}?${paramsString}`
                : window.location.pathname

            window.history.replaceState({}, '', updatedUrl)
        } catch (error) {
            console.error(`Failed to update URL: ${error}`)
        }
    },
    /**
     * Handle head link tags
     */
    handlePrevNextLinkTags() {
        const head = document.head
        const canonicalUrl = document.querySelector('link[rel="canonical"]')?.href

        if (canonicalUrl) {
            head.querySelectorAll('link[rel="prev"], link[rel="next"]')
                .forEach(linkTag => head.removeChild(linkTag))

            this.prevUrl = this.pagesCurrent > 1
                ? this.getPrevUrl(canonicalUrl)
                : null
            this.nextUrl = this.pagesCurrent < this.pagesTotal
                ? this.getNextUrl(canonicalUrl)
                : null

            if (this.prevUrl) {
                head.appendChild(Object.assign(document.createElement('link'), {
                    rel: 'prev',
                    href: this.prevUrl
                }))
            }

            if (this.nextUrl) {
                head.appendChild(Object.assign(document.createElement('link'), {
                    rel: 'next',
                    href: this.nextUrl
                }))
            }
        }
    },
    /**
     * Get the previous URL
     * @param {string} canonicalUrl
     * @returns {string}
     */
    getPrevUrl(canonicalUrl) {
        const url = new URL(canonicalUrl)
        const currentPage = this.pagesCurrent
        if (url.searchParams.has('p')) {
            url.searchParams.set('p', String(currentPage - 1))
            return url.href
        }
    },
    /**
     * Get the next URL
     * @param {string} canonicalUrl
     * @returns {string}
     */
    getNextUrl(canonicalUrl) {
        const url = new URL(canonicalUrl)
        const currentPage = this.pagesCurrent
        if (url.searchParams.has('p')) {
            url.searchParams.set('p', String(currentPage + 1))
            return url.href
        }
    },
    /**
     * Get current page from URL
     *
     * @returns {number|null}
     */
    getCurrentPageFromUrl() {
        const page = parseInt(new URLSearchParams(window.location.search).get('p'), 10)

        return Number.isNaN(page) || page < 1 ? 1 : page
    },
    loadFacetsFromSeoIndexedFilters() {
        // Add SEO indexed filters to selected facets
        Object.entries(this.seoIndexedFilters).forEach(([attribute, options]) => {
            const decodedAttribute = this.safeDecode(attribute)
            this.normalizeFacetOptions(options).forEach(option => {
                this.addFacet(decodedAttribute, option)
            })
        })
    },
    /**
     * Load facets from URL parameters
     * Exclude non facets parameters
     */
    loadFacetsFromUrl() {
        // Reset pending facets collection for this page load
        this.pendingUrlFacets = []

        const rawParams = this.getRawQueryParams()

        if (rawParams.length === 0) {
            return
        }

        const pendingByAttribute = new Map()

        rawParams.forEach(({ key, value }) => {
            if (!key) {
                return
            }

            const decodedAttribute = this.safeDecode(key.replace(/\+/g, ' '))

            // Pagination parameter is not a filter
            if (decodedAttribute === 'p') {
                return
            }

            const normalizedOptions = this.normalizeFacetOptionsFromRaw(value)

            if (normalizedOptions.length === 0) {
                return
            }

            const existing = pendingByAttribute.get(decodedAttribute) || []
            pendingByAttribute.set(decodedAttribute, existing.concat(normalizedOptions))
        })

        pendingByAttribute.forEach((options, attribute) => {
            const uniqueOptions = Array.from(new Set(options))
            this.pendingUrlFacets.push({ attribute, options: uniqueOptions })
        })
    },
    /**
     * Apply pending URL facets after allowlist is known.
     *
     * @param {Array} facets
     * @returns {boolean}
     */
    applyPendingUrlFacetsFromAllowlist(facets) {
        this.setFacetAttributeAllowlist(facets)

        if (this.hasAppliedUrlFacets) {
            return false
        }

        this.hasAppliedUrlFacets = true

        if (!this.pendingUrlFacets.length) {
            return false
        }

        let applied = false

        this.pendingUrlFacets.forEach(({ attribute, options = [] }) => {
            if (!this.isFacetAttributeAllowed(attribute)) {
                return
            }

            options.forEach(option => this.addFacet(attribute, option))
            applied = true
        })

        this.pendingUrlFacets = []

        return applied
    },
    /**
     * Update the URL with the current page number.
     * This is used to update the URL when the user scrolls to a new page.
     * @param {number} page
     */
    setUrlPageParam(page) {
        const normalizedPage = Math.max(parseInt(page, 10) || 1, 1)
        this.pagesCurrent = normalizedPage

        const newUrl = new URL(window.location.href)

        if (!(this.urlParams instanceof URLSearchParams)) {
            this.urlParams = new URLSearchParams(newUrl.search)
        }

        // Only add the page param starting from page 2; keep URL clean on page 1
        if (normalizedPage > 1) {
            this.urlParams.set('p', String(normalizedPage))
        } else {
            this.urlParams.delete('p')
        }

        newUrl.search = this.urlParams.toString()

        // Only update history state if not already the same
        if (window.location.search !== newUrl.search) {
            window.history.replaceState({}, '', newUrl)
        }
    }
}

export default liveSearchUrl
